import { useState, useCallback } from 'react';
import { B, radius, space } from '../../constants/brand';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { Btn, Card, Input, Icon, Spinner, ErrorMsg, Modal, ModalFooter,
         Badge, clickable, useToast } from '../ui';

// =============================================================
// SageExcludedAccountsPanel — the GL accounts the Sage population leaves out.
//
// This is the screen for dbo.SageExcludedAccounts (migration 186). Before it,
// the list was a literal in dbo.vw_SageTransactionLines' WHERE clause and every
// addition cost a release; the table made it data and these controls are what
// make it editable by the people who actually recognise a payables account.
//
// IT IS A SMALL LIST ATTACHED TO A VERY LARGE LEVER, and the copy is most of
// the work here. The view is the shared population for every Sage read path, so
// one row changes the debt breakout, DEPLOYED CAPITAL, both IRRs and the
// deal-detail Transactions tab at once. A bare table of six numbers with a
// delete button beside each would give no reader any way to know that.
//
// THE PANEL COMPUTES NOTHING ABOUT AN ACCOUNT NUMBER, deliberately. Whether
// 21116 is inside the view's population, or lands in [Assets] rather than
// [Debt], is the view's business — restating those ranges here is exactly the
// drift the API's own guards forbid ("neither client owns a table that could
// drift from the ranges"). So per-account judgements come from the server as
// `warning`, and the panel states the general rule in words. The one thing it
// compares locally is `accountNumber` against the API's `seeded` array, which
// is data the API sent, not a rule.
//
// REMOVING IS THE DANGEROUS DIRECTION, not adding. Excluding an account takes
// money out of a figure that was arguably overstated; re-admitting one PUTS
// MONEY BACK IN, silently, across every deal that posts to it. So removal is
// the only action behind a confirm, and the confirm names what comes back.
// =============================================================

const noteBox = (tone) => {
  const t = {
    info:    { bg: '#f8fafc', border: '#e2e8f0', color: B.textMd },
    success: { bg: '#f0fdf4', border: '#bbf7d0', color: B.green },
    warn:    { bg: '#fffbeb', border: '#fde68a', color: B.amber },
    error:   { bg: '#fef2f2', border: '#fecaca', color: B.red },
  }[tone] || {};
  return {
    marginTop: 10, padding: '8px 12px', borderRadius: radius.md,
    fontSize: 12, background: t.bg, color: t.color, border: `1px solid ${t.border}`,
  };
};

const mono = { fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: B.text };

// A refresh result reads as reassurance when it worked and as a caveat when it
// did not — a figure that lags its own screen for a day is worth a sentence.
const RefreshNote = ({ refresh }) => {
  if (!refresh) return null;
  if (!refresh.failed?.length) {
    return <div style={noteBox('success')}>Cached deal IRRs and the Capital Deployment snapshot have been recomputed.</div>;
  }
  return (
    <div style={noteBox('warn')}>
      The change is live, but {refresh.failed.map(f => f.job).join(' and ')} could not be
      recomputed ({refresh.failed.map(f => f.detail).join('; ')}). Cached deal IRRs and the
      Capital Deployment snapshot will catch up on the next Sage import or the daily recompute.
    </div>
  );
};

const SageExcludedAccountsPanel = () => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);

  // Fetched only once the panel is opened: it is one of several on this page and
  // a collapsed panel has been read by nobody.
  const { data, loading, error, reload } = useApi(
    useCallback(() => (open ? api.get('sage/excluded-accounts') : Promise.resolve(null)), [open]),
    [open],
  );

  const [acct,    setAcct]    = useState('');
  const [reason,  setReason]  = useState('');
  const [adding,  setAdding]  = useState(false);
  const [result,  setResult]  = useState(null);   // last add/remove outcome
  const [err,     setErr]     = useState(null);
  const [confirm, setConfirm] = useState(null);   // the row awaiting removal
  const [removing, setRemoving] = useState(false);

  const rows   = data?.accounts || [];
  const seeded = data?.seeded || [];
  // A row the platform shipped with, as opposed to one somebody added since.
  const isSeeded = (n) => seeded.includes(Number(n));

  const add = async () => {
    setAdding(true); setErr(null); setResult(null);
    try {
      // The write kicks two recomputes server-side (the same ones a GL import
      // fires), so it outlasts the client's 10s default by a long way.
      const r = await api.post('sage/excluded-accounts',
        { accountNumber: acct.trim(), reason: reason.trim() || undefined },
        { timeout: 600000 });
      setResult(r);
      if (r?.added) { setAcct(''); setReason(''); toast(`Account ${r.accountNumber} excluded`); }
      await reload();
    } catch (e) {
      // Shown verbatim: the API names what it refused and why, and inventing a
      // client-side message here would throw that away.
      setErr(e.message || 'Could not exclude the account');
    } finally { setAdding(false); }
  };

  const remove = async () => {
    setRemoving(true); setErr(null); setResult(null);
    try {
      const r = await api.del(`sage/excluded-accounts/${confirm.accountNumber}`, { timeout: 600000 });
      setResult(r);
      toast(`Account ${confirm.accountNumber} is back in the Sage population`);
      setConfirm(null);
      await reload();
    } catch (e) {
      setErr(e.message || 'Could not remove the exclusion');
    } finally { setRemoving(false); }
  };

  return (
    <Card style={{ marginBottom: 24 }}>
      <div {...clickable(() => setOpen(!open))} aria-expanded={open}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: B.tealDk, display: 'inline-flex' }}><Icon name="filter" size={18} /></span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: B.text }}>Excluded GL accounts</div>
            <div style={{ fontSize: 12, color: B.textMd }}>
              Accounts that are in the ledger but are not a deal’s capital — kept out of every cost figure.
            </div>
          </div>
        </div>
        <Icon name={open ? 'chevronUp' : 'chevronDown'} size={16} />
      </div>

      {open && (
        <div style={{ marginTop: 14 }}>
          {/* WHAT AN EDIT DOES, up front. This is the part a table of numbers
              cannot say, and the reason the panel is more copy than control. */}
          <div style={{ fontSize: 12, color: B.textMd, marginBottom: 12, lineHeight: 1.55 }}>
            An account listed here is dropped from the shared Sage population, so it counts toward{' '}
            <strong>nothing</strong>: not the debt breakout, not deployed capital, not either IRR, and it
            does not appear on a capital item’s Transactions tab. That is deliberate — a payable is not
            borrowing, and its offsetting entry is already counted on the asset or expense side, so
            removing it from the debt figure alone would leave its credit netting against the asset
            inside deployed capital with nothing on screen saying why.
            <div style={{ marginTop: 6 }}>
              Changes take effect on the next page load, and saving recomputes the cached deal IRRs and
              the Capital Deployment snapshot for you. <strong>No release needed.</strong>
            </div>
          </div>

          {loading && <Spinner label="Loading excluded accounts…" />}

          {/* Pre-186 is not an error — the endpoint says what is missing. */}
          {!loading && data && data.available === false && (
            <div style={noteBox('warn')}>{data.error}</div>
          )}
          {!loading && error && <ErrorMsg msg={error} onRetry={reload} />}

          {!loading && data?.available && (
            <>
              <div style={{
                display: 'grid', gridTemplateColumns: '90px 1fr auto', gap: '0 12px',
                alignItems: 'baseline', fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
                textTransform: 'uppercase', color: B.textMd,
                borderBottom: `1px solid ${B.border}`, paddingBottom: 6, marginBottom: 4,
              }}>
                <div>Account</div><div>Why</div><div />
              </div>

              {rows.length === 0 && (
                // An empty list is not a neutral state: 12006 and 21000 leaving
                // the population re-admits clearing and payables balances into
                // every deal roll-up, so it gets said rather than shown blank.
                <div style={noteBox('error')}>
                  Nothing is excluded. Clearing and accounts-payable balances are counting toward
                  deployed capital and both IRRs. Re-run migrations to restore the shipped set, or add
                  the accounts back below.
                </div>
              )}

              {rows.map(r => (
                <div key={r.exclusionID} style={{
                  display: 'grid', gridTemplateColumns: '90px 1fr auto', gap: '0 12px',
                  alignItems: 'center', padding: '7px 0',
                  borderBottom: `1px solid ${B.border}`,
                }}>
                  <div style={mono}>{r.accountNumber}</div>
                  <div style={{ fontSize: 12, color: B.text, lineHeight: 1.45 }}>
                    {r.reason || <span style={{ color: B.textLt }}>No reason recorded</span>}
                    <div style={{ fontSize: 11, color: B.textLt, marginTop: 2 }}>
                      {isSeeded(r.accountNumber)
                        ? <Badge color={B.navy}>Shipped with the platform</Badge>
                        : <>Added by {r.createdBy}</>}
                    </div>
                  </div>
                  <Btn size="sm" variant="secondary" onClick={() => { setConfirm(r); setErr(null); setResult(null); }}>
                    Remove
                  </Btn>
                </div>
              ))}

              {/* ── Add ── */}
              <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${B.border}` }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: B.textMd, marginBottom: 8 }}>
                  Exclude another account
                </div>
                <div style={{ display: 'flex', gap: space.md, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ width: 140 }}>
                    <Input label="Account number" value={acct} placeholder="21116"
                      onChange={setAcct}
                      onKeyDown={(e) => { if (e.key === 'Enter' && acct.trim() && !adding) add(); }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <Input label="Why (recommended)" value={reason} placeholder="Payables — not borrowing"
                      onChange={setReason}
                      onKeyDown={(e) => { if (e.key === 'Enter' && acct.trim() && !adding) add(); }} />
                  </div>
                  <Btn size="sm" disabled={!acct.trim() || adding} onClick={add}>
                    {adding ? 'Excluding…' : 'Exclude account'}
                  </Btn>
                </div>
                <div style={{ fontSize: 11, color: B.textLt, marginTop: 6 }}>
                  A reason is the only thing that will tell the next person what this row is for — the
                  clearing and payables rows above are load-bearing, and someone who cannot tell what
                  one is for is someone who might remove it.
                </div>
              </div>

              {err && <div style={noteBox('error')}>{err}</div>}

              {/* An add that changed nothing must not read as success. */}
              {result?.added === false && <div style={noteBox('info')}>{result.message}</div>}
              {result?.warning && <div style={noteBox('warn')}>{result.warning}</div>}
              {result?.note && <div style={noteBox('warn')}>{result.note}</div>}
              {(result?.added || result?.removed) && <RefreshNote refresh={result.refresh} />}
            </>
          )}
        </div>
      )}

      {confirm && (
        <Modal title={`Put account ${confirm.accountNumber} back in the population?`}
          onClose={() => setConfirm(null)} width={520}>
          <div style={{ fontSize: 13, color: B.text, lineHeight: 1.6 }}>
            <p style={{ marginTop: 0 }}>
              This is the direction that <strong>adds</strong> money to figures. Account{' '}
              <span style={mono}>{confirm.accountNumber}</span> will start counting toward deployed
              capital and both IRRs again on every deal that posts to it — and toward Total Cost too if
              it is an asset, income or expense account.
            </p>
            {confirm.reason && (
              <p style={{ background: B.surfaceMuted, borderRadius: radius.lg, padding: '8px 12px', fontSize: 12, color: B.textMd }}>
                Recorded reason: {confirm.reason}
              </p>
            )}
            {isSeeded(confirm.accountNumber) && (
              // The two rows nothing should touch casually. Informed consent
              // rather than a block — the API does not forbid it either, and an
              // arbitrary undeletable row is its own kind of trap.
              <div style={noteBox('error')}>
                This account shipped with the platform and has been in the population rule since
                migration 167. Every cost figure on the platform depends on it being excluded. Remove
                it only if you are certain Sage has repurposed the account number.
              </div>
            )}
            <p style={{ fontSize: 12, color: B.textMd, marginBottom: 0 }}>
              You can exclude it again afterwards; nothing is destroyed. Cached IRRs and the Capital
              Deployment snapshot are recomputed either way.
            </p>
          </div>
          <ModalFooter>
            <Btn variant="secondary" onClick={() => setConfirm(null)} disabled={removing}>Cancel</Btn>
            <Btn onClick={remove} disabled={removing}>
              {removing ? 'Removing…' : `Put ${confirm.accountNumber} back`}
            </Btn>
          </ModalFooter>
        </Modal>
      )}
    </Card>
  );
};

export default SageExcludedAccountsPanel;
